import React, { useState, useEffect, useContext } from "react";
import VendorListTable from './VendorListTable';
import VendorForm from './VendorForm';
import './vendor.css';
import MyContext from "../../../MyContext";
import { Plus, RefreshCw } from 'lucide-react';
import CreatePurchaseModal from "./CreatePurchaseModal";
import { useNavigate } from 'react-router-dom';
import loader from "../../../images/Anlook.svg";


const Loader = () => {
  return (
    <div className="m-5 p-5 d-flex justify-content-center">
      <div className="d-flex flex-column align-items-center justify-content-center">
        <div className="p-3">
          <div className="loader"></div>
        </div>
        <div className="p-3">
          <img src={loader} alt="loading" height={40} />
        </div>
      </div>
    </div>
  );
};
const VendorManagement = ({ userID }) => {
  const [formMode, setFormMode] = useState(null); // 'add' | 'edit'
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseVendor, setPurchaseVendor] = useState(null);

  const { baseUrl } = useContext(MyContext);
  const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";
  const navigate = useNavigate();
  const fetchVendors = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getVendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ userid: userID }),
      });
      const data = await res.json();
      if (data?.responseCode === '00') {
        setVendors(data.response);
      } else {
        console.warn('Get vendors failed:', data);
         setVendors([]);
      }
    } catch (err) {
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addVendor = async (vendorData) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/addVendor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ ...vendorData, userid: userID }),
      });
      const data = await res.json();
      if (data?.responseCode === '00') {
        fetchVendors();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const editVendor = async (vendorData) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/editVendor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ ...vendorData, userid: userID }),
      });
      const data = await res.json();
      if (data?.responseCode === '00') {
        fetchVendors();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/deleteVendor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ userid: userID, vendorId }),
      });
      const data = await res.json();
      if (data?.responseCode === '00') {
        fetchVendors();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const handleCreatePurchaseClick = (vendor) => {
    navigate('/Inventory/Vendors/CreatePurchase', {
      state: { vendor }, // vendor object gets passed
    });
  };

  const handleSubmitPurchase = async (data) => {
    try {
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/createPurchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ ...data, userid: userID }),
      });
      const result = await res.json();
      alert(result.responseMessage || "Purchase Created");
      setShowPurchaseModal(false);
    } catch (err) {
      console.error(err);
      alert("Error creating purchase");
    }
  };


  useEffect(() => {
    fetchVendors();
  }, [userID]);

  const handleAddClick = () => {
    setFormMode('add');
    setSelectedVendor(null);
  };

  const handleEditClick = (vendor) => {
    setFormMode('edit');
    // ✅ Fix: ensure vendorId is included for edit
    setSelectedVendor({ ...vendor, vendorId: vendor.id || vendor.vendorId });
  };

  const closeForm = () => {
    setFormMode(null);
    setSelectedVendor(null);
  };

  return (
    <div className="vendor-management-wrapper">
      <div className="vendor-header">
        <h2 className="CreateCampaign">Vendor Management</h2>
        <div className="d-flex gap-2">
          <button className="vendor-btn-add" onClick={handleAddClick} disabled={loading}>
            <Plus size={16} /> <span>Add Vendor</span>
          </button>
          <button className="vendor-btn-refresh" onClick={fetchVendors} disabled={loading}>
            <RefreshCw size={16} /> <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="vendor-search-bar">
        <input
          type="text"
          placeholder="Search vendors by name, type or category..."
          className="vendor-search-input-modern"
          disabled={loading}
        />
      </div>

      {loading ? <Loader /> : (
        <div className="vendor-layout">
          <div className="vendor-left-panel">
            <VendorListTable
              vendors={vendors}
              onEdit={handleEditClick}
              onDelete={deleteVendor}
              onCreatePurchase={handleCreatePurchaseClick}
            />
          </div>
          <div className="vendor-right-panel">
            {formMode && (
              <VendorForm
                mode={formMode}
                vendor={selectedVendor}
                onClose={closeForm}
                onSave={formMode === 'add' ? addVendor : editVendor}
              />
            )}
          </div>
          {showPurchaseModal && purchaseVendor && (
            <CreatePurchaseModal
              vendor={purchaseVendor}
              userID={userID}
              baseUrl={baseUrl}
              BASIC_AUTH={BASIC_AUTH}
              onClose={() => setShowPurchaseModal(false)}
            />
          )}
        </div>
      )}



    </div>
  );
};

export default VendorManagement;
